import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Eye, EyeOff, Leaf, ArrowRight } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_circularity-hub/artifacts/hwzjkab2_DecarbonIQ%20%2814%29.png";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const user = await login(form.email, form.password);
      toast.success(`Welcome back, ${user.first_name}`);

      // ── Routing protocol v2: backend-driven destination only ────────────
      // NEVER use stale localStorage; NEVER guess from frontend state.
      // Use server-provided routing context exclusively.
      if (user.role === "admin") {
        window.location.replace("/admin");
        return;
      }

      // Use backend-computed landing_route (from routing context or legacy field)
      const routing = user.routing || {};
      const destination =
        routing.landing_route ||           // v2 protocol (explicit, deterministic)
        user.default_workspace_path ||     // v1 legacy fallback
        "/dashboard";                      // fail-closed neutral

      // Use window.location.replace to force a full context reset —
      // prevents stale PublicOnlyRoute from redirecting to old user's workspace
      window.location.replace(destination);
    } catch (err) {
      setError(err.response?.data?.detail || "Invalid credentials. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex font-sans">
      {/* Left Panel */}
      <div className="w-full lg:w-1/2 flex flex-col justify-center px-8 sm:px-16 bg-white">
        <div className="max-w-md w-full mx-auto">
          {/* Logo */}
          <div className="mb-10">
            <Link to="/" className="inline-flex items-center">
              <div className="bg-[#0F172A] rounded-lg px-3 py-2">
                <img src={LOGO_URL} alt="DeCarbonIQ" className="h-8 w-auto" style={{ filter: "brightness(0) invert(1)" }} />
              </div>
            </Link>
          </div>

          <h1 className="text-3xl font-semibold text-[#0F172A] tracking-tight mb-2">Welcome back</h1>
          <p className="text-slate-500 text-sm mb-8">Sign in to your climate intelligence dashboard.</p>

          {error && (
            <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700" data-testid="login-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
            <div>
              <label className="block text-xs font-medium uppercase tracking-wider text-slate-500 mb-1.5">Email Address</label>
              <input
                type="email"
                value={form.email}
                onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                className="w-full h-10 px-3 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#059669] focus:border-transparent transition-colors"
                placeholder="you@organization.com"
                required
                data-testid="login-email-input"
              />
            </div>
            <div>
              <label className="block text-xs font-medium uppercase tracking-wider text-slate-500 mb-1.5">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={form.password}
                  onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                  className="w-full h-10 px-3 pr-10 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#059669] focus:border-transparent transition-colors"
                  placeholder="••••••••"
                  required
                  data-testid="login-password-input"
                />
                <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" onClick={() => setShowPassword(!showPassword)}>
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full h-10 bg-[#0F172A] text-white text-sm font-medium rounded-lg hover:bg-[#1E293B] transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              data-testid="login-submit-btn"
            >
              {loading ? (
                <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Signing in...</>
              ) : (
                <><span>Sign In</span> <ArrowRight size={14} /></>
              )}
            </button>
          </form>

          <div className="mt-6 pt-6 border-t border-slate-200">
            <p className="text-sm text-slate-500 text-center">
              Don't have an account?{" "}
              <Link to="/signup" className="text-[#059669] font-medium hover:underline" data-testid="signup-link">
                Create account
              </Link>
            </p>
          </div>

          {/* Contact info */}
          <div className="mt-4 text-center">
            <p className="text-xs text-slate-400">
              Need access? <a href="mailto:info@decarboniq.com" className="text-teal-600 font-semibold hover:underline">info@decarboniq.com</a>
            </p>
          </div>
        </div>
      </div>

      {/* Right Panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#0F172A] flex-col justify-between p-12">
        <div className="flex items-center gap-2">
          <Leaf size={16} className="text-emerald-400" />
          <span className="text-xs font-medium text-slate-400 uppercase tracking-widest">Climate Intelligence Platform</span>
        </div>
        <div className="max-w-sm">
          <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6 mb-8">
            <div className="flex items-center justify-between mb-4">
              <span className="text-xs text-slate-400 uppercase tracking-wider">Climate Score</span>
              <span className="text-xs px-2 py-0.5 bg-emerald-900/50 text-emerald-400 rounded-full font-medium">Verified</span>
            </div>
            <div className="text-4xl font-bold text-white mb-1 tabular-nums">72<span className="text-lg text-slate-400 font-normal">/100</span></div>
            <p className="text-sm text-slate-400">GHG Protocol Corporate Standard</p>
            <div className="mt-4 space-y-2">
              {[["Total Emissions", "97.6 tCO₂e"], ["Evidence Coverage", "75%"], ["Data Confidence", "80%"]].map(([k, v]) => (
                <div key={k} className="flex justify-between text-xs">
                  <span className="text-slate-500">{k}</span>
                  <span className="text-white font-medium">{v}</span>
                </div>
              ))}
            </div>
          </div>
          <h2 className="text-2xl font-semibold text-white mb-3 tracking-tight">Institutional-grade climate intelligence.</h2>
          <p className="text-slate-400 text-sm leading-relaxed">Track emissions, generate credible reports, and publish to the public registry. Built for Africa and emerging markets.</p>
        </div>
        <div className="text-xs text-slate-600">© 2024 DeCarbonIQ — All rights reserved</div>
      </div>
    </div>
  );
}
